import ClientComponent from '@/components/ClientComponent/ClientComponent';
import { ChannelVideo } from '@/typings/channelVideo';
import { Locale } from '@/typings/locale';
import { VideoCategory } from '@/typings/videoCategory';
import { firebaseAdmin } from '@/utils/firebaseAdmin';
import ChannelVideosList from '@/views/ChannelVideosList/ChannelVideosList';
import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';

type ChannelVideoDetailProps = {
  channelVideos: ChannelVideo[];
  categories: VideoCategory[];
  locale: Locale;
};

function ChannelVideoDetail({ channelVideos, categories }: ChannelVideoDetailProps) {
  const { locale } = useRouter();
  return (
    <>
      <NextSeo title={locale === 'en-US' ? 'Channel' : 'Canal'} />
      <ClientComponent>
        <ChannelVideosList {...{ channelVideos, categories }} />
      </ClientComponent>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (context) => {
  const db = firebaseAdmin.firestore();
  const channelVideosCollection = db.collection('channelVideos');
  const { docs } = await channelVideosCollection
    .where('deletedAt', '==', null)
    .orderBy('order', 'asc')
    .get();

  const channelVideos: ChannelVideo[] = (docs || []).map((doc) => {
    return {
      ...(doc.data() as ChannelVideo),
      id: doc.id,
    };
  });

  if (!channelVideos) {
    return {
      notFound: true,
    };
  }

  const videoCategoryCollection = db.collection('videoCategories');

  const { docs: categoryDocs } = await videoCategoryCollection
    .where('deletedAt', '==', null)
    .orderBy('order', 'asc')
    .get();

  const categories: VideoCategory[] = (categoryDocs || [])
    .map((doc) => ({
      ...(doc.data() as VideoCategory),
      id: doc.id,
      videos: channelVideos.filter((video) => video.categoryId === doc.id),
    }))
    .filter((category) => !!category.videos.length);

  if (!categories) {
    return {
      props: {
        channelVideos: JSON.parse(JSON.stringify(channelVideos)),
        categories: [],
        locale: context.locale,
      },
      revalidate: 2592000,
    };
  } else {
    const videosWithCategories = channelVideos.map((video) => {
      const category = categories.find((category) => category.id === video.categoryId);
      return { ...video, category };
    });

    return {
      props: {
        channelVideos: JSON.parse(JSON.stringify(videosWithCategories)),
        categories: JSON.parse(JSON.stringify(categories)),
        locale: context.locale,
      },
      revalidate: 2592000,
    };
  }
};

export default ChannelVideoDetail;
